import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, Lock, Trash2, Download, 
  Server, ArrowLeft,
  CheckCircle2, Camera, Bell, Smartphone
} from 'lucide-react';

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState('philosophy');
  const lastUpdated = "March 2026";

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'philosophy', title: 'Our Privacy Philosophy', icon: Shield },
    { id: 'device-permissions', title: 'Device Permissions', icon: Smartphone },
    { id: 'processing', title: 'How Data is Processed', icon: Server },
    { id: 'your-rights', title: 'Your Rights (GDPR)', icon: Download },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F5F0] to-white">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-primary hover:opacity-80 transition-opacity">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium hidden sm:inline">Back to Home</span>
            <span className="font-medium sm:hidden">Back</span>
          </Link>
          <div className="text-xs sm:text-sm text-muted-foreground">
            Effective Date: {lastUpdated}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Title */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-4">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#1A1A1A] mb-2 sm:mb-4">Privacy Policy</h1>
          <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            The "Stateless" Model
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Navigation Sidebar - Hidden on mobile, shown on lg screens */}
          <div className="hidden lg:block lg:col-span-1">
            <nav className="sticky top-24 space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                    activeSection === section.id
                      ? 'bg-primary text-white'
                      : 'bg-white hover:bg-gray-50 text-[#1A1A1A]'
                  }`}
                >
                  <section.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 space-y-6 sm:space-y-8">
            {/* Our Privacy Philosophy */}
            <motion.section 
              id="philosophy"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-sm border border-border"
            >
              <h2 className="text-lg sm:text-2xl font-bold text-[#1A1A1A] mb-4 sm:mb-6 flex items-center gap-3">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                Our Privacy Philosophy
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="text-base sm:text-lg">
                  At Vitala.me, we believe <strong>your health data is your own</strong>.
                </p>
                <p className="text-sm sm:text-base">
                  We do not pull data from wearables (Apple Health, Oura, etc.) and only process 
                  what you manually share with us.
                </p>
              </div>
            </motion.section>

            {/* How Your Data is Processed */}
            <section id="processing" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-sm border border-border">
              <h2 className="text-lg sm:text-2xl font-bold text-[#1A1A1A] mb-4 sm:mb-6 flex items-center gap-3">
                <Server className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                How Your Data is Processed (Stateless AI)
              </h2>
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-blue-800 text-sm sm:text-base">Inference Only</p>
                        <p className="text-blue-700 text-xs sm:text-sm mt-1">
                          Your health data is sent to our AI engine (GPT-5.2 via Emergent LLM) only at the 
                          moment you request a recommendation.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-blue-800 text-sm sm:text-base">No Training</p>
                        <p className="text-blue-700 text-xs sm:text-sm mt-1">
                          We do <strong>not</strong> use your personal health data to train or "fine-tune" our AI models. 
                          Once the recommendation is generated, the AI "forgets" the specific interaction.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-blue-800 text-sm sm:text-base">Storage</p>
                        <p className="text-blue-700 text-xs sm:text-sm mt-1">
                          Your account details and quiz history are stored securely on a MongoDB instance 
                          via the Emergent Platform.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Device Permissions - NEW SECTION */}
            <section id="device-permissions" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-sm border border-border">
              <h2 className="text-lg sm:text-2xl font-bold text-[#1A1A1A] mb-4 sm:mb-6 flex items-center gap-3">
                <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                Device Permissions & Data Collection
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-sm sm:text-base">
                  Vitala may request access to certain device features to provide enhanced functionality. 
                  Here's exactly what we request and why:
                </p>
                
                <div className="space-y-4">
                  {/* Camera Permission */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Camera className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-900">Camera Access</h3>
                    </div>
                    <p className="text-sm text-blue-800 mb-3">
                      <strong>Purpose:</strong> To scan barcodes on supplement bottles for instant product identification and interaction checks.
                    </p>
                    <div className="bg-white/50 rounded-lg p-3">
                      <p className="text-xs text-blue-700 font-medium mb-2">How we handle camera data:</p>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Images are processed <strong>locally on your device only</strong></li>
                        <li>• We <strong>never upload, store, or transmit</strong> your camera feed</li>
                        <li>• Barcode data is converted to text for supplement lookup</li>
                        <li>• You can revoke camera access anytime in your device settings</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Notifications Permission */}
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Bell className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-purple-900">Push Notifications</h3>
                    </div>
                    <p className="text-sm text-purple-800 mb-3">
                      <strong>Purpose:</strong> To send you supplement reminders and health tips at times you choose.
                    </p>
                    <div className="bg-white/50 rounded-lg p-3">
                      <p className="text-xs text-purple-700 font-medium mb-2">What we send:</p>
                      <ul className="text-xs text-purple-700 space-y-1">
                        <li>• Supplement intake reminders (times you configure)</li>
                        <li>• Optional weekly health tips (can be disabled)</li>
                        <li>• We <strong>never</strong> send promotional or advertising notifications</li>
                        <li>• You control all notification preferences in Settings</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Local Storage */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Lock className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">Local Storage (Offline Access)</h3>
                    </div>
                    <p className="text-sm text-green-800 mb-3">
                      <strong>Purpose:</strong> To cache your recommendations for offline viewing and save assessment progress.
                    </p>
                    <div className="bg-white/50 rounded-lg p-3">
                      <p className="text-xs text-green-700 font-medium mb-2">What we store locally:</p>
                      <ul className="text-xs text-green-700 space-y-1">
                        <li>• Your supplement recommendations (for offline access)</li>
                        <li>• Assessment progress (so you don't lose work)</li>
                        <li>• Login session (so you don't have to sign in repeatedly)</li>
                        <li>• All local data can be cleared by logging out or clearing browser data</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-muted-foreground">
                    <strong>Your Control:</strong> All permissions are optional. You can use Vitala's core features 
                    (assessment, recommendations, Ask AI) without granting any device permissions. Enhanced features 
                    like barcode scanning require camera access.
                  </p>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section id="your-rights" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-sm border border-border">
              <h2 className="text-lg sm:text-2xl font-bold text-[#1A1A1A] mb-4 sm:mb-6 flex items-center gap-3">
                <Download className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                Your Rights (GDPR & Data Sovereignty)
              </h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-4 sm:p-5">
                    <div className="flex items-start gap-3">
                      <Download className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-800 text-sm sm:text-base">Data Export</p>
                        <p className="text-green-700 text-xs sm:text-sm mt-1">
                          You may download a full JSON export of your profile at any time from your Account Settings.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-4 sm:p-5">
                    <div className="flex items-start gap-3">
                      <Trash2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-red-800 text-sm sm:text-base">Right to Erasure</p>
                        <p className="text-red-700 text-xs sm:text-sm mt-1">
                          Deleting your account permanently removes all health assessment data from our active databases.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-lg sm:rounded-xl p-4 sm:p-5">
                    <div className="flex items-start gap-3">
                      <Lock className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-purple-800 text-sm sm:text-base">No Third-Party Sale</p>
                        <p className="text-purple-700 text-xs sm:text-sm mt-1">
                          We <strong>never</strong> sell your health profiles to supplement manufacturers or advertisers.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground">
                  To exercise any of these rights, please visit your{' '}
                  <Link to="/profile" className="text-primary hover:underline font-medium">
                    Account Settings
                  </Link>{' '}
                  or contact us at{' '}
                  <a href="mailto:privacy@vitala.me" className="text-primary hover:underline">
                    privacy@vitala.me
                  </a>
                </p>
              </div>
            </section>

            {/* Last Updated */}
            <div className="text-center text-xs sm:text-sm text-muted-foreground py-6 sm:py-8">
              This Privacy Policy is effective as of {lastUpdated}.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
